import { AxiosError, isAxiosError } from 'axios'
import {
  PydanticValidationError,
  MarshmallowValidationError,
  UserRequestError,
} from 'api/types'

export const DEFAULT_DISPLAY_MESSAGE =
  "Hmm.. looks like things aren't working. " +
  'Please check your internet connection. ' +
  'If you require assistance, please contact us.'

/**
 * UserRequestError
 * Standard error type by the backend to set a display message for Bad Requests that are resolvable by the user.
 *
 * @example
 * {
 *   message: {
 *     general: ["Example user request error message"]
 *   },
 *   error_type?: "needs_phone_verification"
 * }
 */
const isUserRequestOrHandledError = (data: unknown): data is UserRequestError =>
  Array.isArray((data as UserRequestError)?.message?.general)

/**
 * marshmallow.ValidationError
 * Legacy standard error type by the backend when the request body is invalid.
 *
 * @example
 * {
 *   message: {
 *     fieldName: ["Error message"]
 *     fieldName2: ["Error message 2"]
 *   }
 * }
 */
const isMarshmallowValidationError = (
  data: unknown
): data is MarshmallowValidationError => {
  const message = (data as MarshmallowValidationError)?.message
  return (
    typeof message === 'object' &&
    message !== null &&
    !Array.isArray(message.general)
  )
}

/**
 * Pydantic — RequestValidationError
 * Current standard error type by the backend when the request body is invalid.
 *
 * @example
 * {
 *   errors: [
 *     { loc: ["body", "name"], msg: "Name is required.", type: "value_error" },
 *     { loc: ["body", "age"], msg: "Age must be a positive integer.", type: "value_error" }
 *   ]
 * }
 */
const isPydanticValidationError = (
  data: unknown
): data is PydanticValidationError =>
  Array.isArray((data as PydanticValidationError)?.errors)

function getMessageString(response: AxiosError['response']): string {
  const data = response?.data

  if (data instanceof Error) {
    return data.message
  }

  if (typeof data === 'string') {
    return data
  }

  // Handled API errors
  if (typeof data === 'object' && data !== null) {
    if (isUserRequestOrHandledError(data)) {
      return data.message.general[0]
    }

    if (isMarshmallowValidationError(data)) {
      return Object.values(data.message).flat().join('. ')
    }

    if (isPydanticValidationError(data)) {
      return data.errors.map((e) => e.msg).join('. ')
    }
  }

  // Do not attempt to extract unknown shapes;
  // they are probably not user-friendly error messages
  return ''
}

export const parseErrorMessage = (error: unknown) => {
  if (!isAxiosError(error)) {
    return Promise.reject(new Error(DEFAULT_DISPLAY_MESSAGE, { cause: error }))
  }

  const displayMessage = getMessageString(error.response)
  if (displayMessage) {
    return Promise.reject(new Error(displayMessage, { cause: error }))
  }

  return Promise.reject(new Error(DEFAULT_DISPLAY_MESSAGE, { cause: error }))
}
